/**
 * `useChannelEndpoints` — the LIVE two-endpoint state of an IM channel, for the
 * connect dialog's auto-detection (operator UX feedback 2026-07-26, relayed by
 * `lexruntime-whatsapp`).
 *
 * A channel is usable only when BOTH ends are closed (see `lexiun-user-setup.md`
 * §2-bis): endpoint 1 = the assistant's OWN door (its WhatsApp link / Telegram
 * bot), endpoint 2 = the lawyer's number bound as the command door. The connect
 * dialog must reflect the TRUE per-endpoint state so a lawyer who just scanned
 * the QR sees "step 1 done — now do step 2" instead of a static screen.
 *
 * ## Why endpoint 1 keys off `channels.status`, NOT `channelComplete`
 *
 * The obvious signal — `setup.status.channelComplete` / `botChannels` — is
 * UNRELIABLE: `getBotChannels` reads the openclaw.json CONFIG accounts, which do
 * not reflect a live Baileys link, so a genuinely-connected WhatsApp reads
 * `channelComplete=false` (a runtime-side staleness the `lexruntime-whatsapp`
 * lane is fixing). The authoritative LIVE signal is the gateway probe
 * `channels.status {probe:true}` → `channels[ch].connected && linked` (WhatsApp)
 * / `connected && probe.ok` (Telegram). This module keys off that; once the
 * staleness fix lands, `channelComplete` becomes a valid secondary signal but
 * `channels.status` stays the durable one.
 *
 * Endpoint 2 is the owner command-door binding — `hasCommandDoor(owner, ch)`
 * over `identity.get`, the same read `OwnerPairingPanel` already uses.
 */
import { useCallback, useEffect, useState } from "react";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { hasCommandDoor } from "@/lib/owner-channel-binding";

export type IMChannel = "whatsapp" | "telegram";

/** The slice of `channels.status {probe:true}` we read. Permissive — an older
 *  runtime may omit fields, in which case the endpoint reads as not-closed
 *  (the conservative direction: never claim "connected" on missing evidence). */
interface LiveChannelRow {
  configured?: boolean;
  running?: boolean;
  connected?: boolean;
  linked?: boolean;
  statusState?: string;
  probe?: { ok?: boolean } | null;
}
interface ChannelsStatusResult {
  channels?: Partial<Record<IMChannel, LiveChannelRow>>;
}

/** Endpoint 1 = the assistant's own door is LIVE (not merely configured).
 *  Exported for unit tests — the per-channel rule is the crux. */
export function endpoint1Live(channel: IMChannel, row: LiveChannelRow | undefined): boolean {
  if (!row) return false;
  if (channel === "whatsapp") return !!(row.connected && row.linked);
  // Telegram: the LIVE wire carries NO `connected` field at all (verified on a
  // real runtime 2026-07-27: `{configured, running, probe:{ok}, mode}`), so
  // requiring it read a healthy bot as down — the SPA-mirror-drift bug class.
  // The getMe probe is authoritative when present; fall back to `running`
  // (with `connected` honored if a future runtime adds it).
  return !!(row.probe?.ok ?? (row.connected ?? row.running));
}

export interface ChannelEndpointsState {
  /** `null` until the first read resolves; then the live per-endpoint truth. */
  ep1: boolean | null;
  ep2: boolean | null;
  loading: boolean;
  /** Force an immediate re-read (e.g. right after the owner pairing binds). */
  refresh: () => void;
}

export function useChannelEndpoints(
  channel: IMChannel,
  opts?: { enabled?: boolean; pollMs?: number },
): ChannelEndpointsState {
  const enabled = opts?.enabled ?? true;
  const pollMs = opts?.pollMs ?? 3000;
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const connected = connectionState === "connected";

  const [ep1, setEp1] = useState<boolean | null>(null);
  const [ep2, setEp2] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  const read = useCallback(async () => {
    if (!rpc || !connected || !enabled) return;
    setLoading(true);
    try {
      const [status, id] = await Promise.all([
        rpc.call<ChannelsStatusResult>("channels.status", { probe: true }),
        rpc.identityGet().catch(() => null),
      ]);
      setEp1(endpoint1Live(channel, status?.channels?.[channel]));
      setEp2(id ? hasCommandDoor(id.owner, channel) : null);
    } catch {
      // A transient read failure must never flip a ✓ back to ✗ — keep the prior
      // verdict and let the next tick re-read.
    } finally {
      setLoading(false);
    }
  }, [rpc, connected, enabled, channel]);

  // Initial read + re-read whenever the socket (re)connects.
  useEffect(() => {
    void read();
  }, [read]);

  // Poll while the channel is not yet fully complete — this is what catches the
  // QR scan (ep1 closing) AND the owner-pairing (ep2 closing) without the user
  // touching anything. Once both ends are closed, stop.
  useEffect(() => {
    if (!enabled || !connected) return;
    if (ep1 === true && ep2 === true) return;
    const t = setInterval(() => void read(), pollMs);
    return () => clearInterval(t);
  }, [enabled, connected, ep1, ep2, pollMs, read]);

  return { ep1, ep2, loading, refresh: () => void read() };
}
